import React, { useState } from 'react';
import { FaSave, FaArrowLeft, FaTag, FaInfo, FaCamera, FaStethoscope, FaFilePdf } from 'react-icons/fa';

import VideoEditBasicInfoSection from '../../components/VideoEditBasicInfoSection';
import VideoEditSpecialtySection from '../../components/VideoEditSpecialtySection/VideoEditSpecialtySection';
import VideoEditTagSection from '../../components/VideoEditTagSection';
import VideoEditImagesSection from '../../components/VideoEditImagesSection';
import VideoEditMaterialsSection from '../../components/VideoEditMaterialsSection';
import ConfirmDialog from '../../components/ConfirmDialog';
import LoadingIndicator from '../../components/LoadingIndicator';

import { useVideoEdit, usePrompt } from './useVideoEdit';
import { ConfirmDialogProvider } from '../../contexts/ConfirmDialogProvider';
import { VideoEditProps } from './types';
import styles from './VideoEdit.module.css';

// VideoEditBasicInfoSectionのための型をインポート
import { VisibilityType } from '../../components/VideoEditBasicInfoSection/types';
// VideoEditImagesSectionのための型をインポート
import { Video as ImageSectionVideo, VideoImage as ImageSectionVideoImage } from '../../components/VideoEditImagesSection/types';

const VideoEditComponent: React.FC = () => {
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // 画像順序変更のハンドラー
  const handleImageOrderChange = (newOrderedImages: ImageSectionVideoImage[]) => {
    // 型変換: ImageSectionVideoImage[] → VideoImage[]
    const convertedImages = newOrderedImages.map(img => ({
      id: img.id,
      video_id: img.video_id || '',
      image_path: img.image_path || '',
      description: img.description || '',
      display_order: img.display_order || 0,
      created_at: img.created_at || '',
      updated_at: img.updated_at || ''
    }));

    // 新しい順序でvideoImagesを更新
    updateImageOrder(convertedImages);
  };

  const {
    id,
    video,
    title,
    setTitle,
    description,
    setDescription,
    visibility,
    setVisibility,
    tags,
    setTags,
    inputValue,
    setInputValue,
    isComposing,
    setIsComposing,
    specialties,
    selectedSpecialtyId,
    handleSpecialtyChange,
    specialtyAttributes,
    setSpecialtyAttributes,
    suggestedKeys,
    videoImages,
    loading,
    isMobile,
    isReorderMode,
    isFormDirty,
    activeTab,
    setActiveTab,
    handleImageFileChange,
    handleDeleteImage,
    handleSave,
    handleReorderModeChange,
    setCommitPendingDeletions,
    // setCommitTempImages, // 新しいフローでは不要
    navigate,
    activeImages,
    pendingDeletions,
    pendingAdditions,
    isImageSaving,
    hasImageChanges,
    isPendingDeletion,
    toggleDeletionStatus,
    removeImage,
    updateImageOrder
  } = useVideoEdit();


  // ページ遷移前の確認メッセージを設定
  usePrompt(
    isFormDirty,
    '変更が保存されていません。このページから移動しますか？'
  );

  if (!video) return (
    <div className={styles['video-edit-loadingContainer']}>
      <LoadingIndicator className={styles['video-edit-loadingIndicator']} />
    </div>
  );

  // VideoEditBasicInfoSectionコンポーネントに渡すためにvideo型を変換
  const basicSectionVideo = {
    id: String(video.id),
    title: video.title || '',
    description: video.description || '',
    visibility: (visibility as VisibilityType),
    file_path: '',
    path: '',
    published_at: video.published_at || null,
    created_at: video.created_at || null
  };

  // VideoEditImagesSectionコンポーネントに渡すためにvideo型を変換
  const imageSectionVideo: ImageSectionVideo = {
    id: String(video.id),
    title: video.title || ''
  };

  // 処理中の動画かどうかを判定
  const isVideoProcessing = String(video.id).startsWith('session_');

  // VideoEditImagesSectionコンポーネントに渡すためにvideoImages型を変換
  // fileプロパティを保持してプレビュー表示を可能にする
  const convertedVideoImages: ImageSectionVideoImage[] = videoImages.map(img => ({
    id: String(img.id),
    image_path: img.image_path || '',
    created_at: img.created_at || new Date().toISOString(),
    display_order: img.display_order,
    description: img.description,
    ...(((img as any).file) && { file: (img as any).file }) // fileプロパティがある場合は保持
  }));

  // モバイル用のタブレンダリング
  const renderMobileTabs = () => {
    if (!isMobile) return null;

    return (
      <div className={styles['video-edit-tabs-container']}>
        <div className={styles['video-edit-tabs']}>
          <button
            className={`${styles['video-edit-tab-btn']} ${activeTab === 'basic' ? styles.active : ''}`}
            onClick={() => setActiveTab('basic')}
          >
            <FaInfo className={styles['video-edit-tab-icon']} />
            <span>基本情報</span>
          </button>
          <button
            className={`${styles['video-edit-tab-btn']} ${activeTab === 'tags' ? styles.active : ''}`}
            onClick={() => setActiveTab('tags')}
          >
            <FaTag className={styles['video-edit-tab-icon']} />
            <span>タグ</span>
          </button>
          <button
            className={`${styles['video-edit-tab-btn']} ${activeTab === 'images' ? styles.active : ''}`}
            onClick={() => setActiveTab('images')}
          >
            <FaCamera className={styles['video-edit-tab-icon']} />
            <span>画像</span>
          </button>
        </div>
      </div>
    );
  };

  // モバイル用のコンテンツレンダリング
  const renderMobileContent = () => {
    if (!isMobile) return null;

    // activeTabに応じて表示するコンポーネントを切り替え
    switch (activeTab) {
      case 'basic':
        return (
          <div className={styles['video-edit-fadeIn']}>
            <VideoEditBasicInfoSection
              title={title}
              setTitle={setTitle}
              description={description}
              setDescription={setDescription}
              visibility={visibility as VisibilityType}
              setVisibility={setVisibility as (v: VisibilityType) => void}
              video={basicSectionVideo}
            />
          </div>
        );
      case 'tags':
        return (
          <div className={styles['video-edit-fadeIn']}>
            <VideoEditSpecialtySection
              specialties={specialties}
              selectedSpecialtyId={selectedSpecialtyId}
              handleSpecialtyChange={handleSpecialtyChange}
              attributes={specialtyAttributes}
              setAttributes={setSpecialtyAttributes}
              suggestedKeys={suggestedKeys}
            />
            <VideoEditTagSection
              tags={tags}
              setTags={setTags}
              inputValue={inputValue}
              setInputValue={setInputValue}
              isComposing={isComposing}
              setIsComposing={setIsComposing}
            />
          </div>
        );
      case 'images':
        return (
          <div className={styles['video-edit-fadeIn']}>
            <VideoEditImagesSection
              video={imageSectionVideo}
              videoImages={convertedVideoImages}
              allImages={convertedVideoImages}
              handleDeleteImage={handleDeleteImage}
              handleImageFileChange={handleImageFileChange}
              onReorderModeChange={handleReorderModeChange}
              toggleDeletionStatus={toggleDeletionStatus}
              isPendingDeletion={isPendingDeletion}
              isVideoProcessing={isVideoProcessing}
            />
            <h3 className={styles['video-edit-sectionTitle']}>
              <FaFilePdf />
              資料
            </h3>
            <VideoEditMaterialsSection
              videoId={String(video.id)}
              isVideoProcessing={isVideoProcessing}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles['video-edit-container']}>
      {loading && (
        <div className={styles['video-edit-loadingOverlay']}>
          <div className={styles['video-edit-loadingSpinner']}></div>
          <p className={styles['video-edit-loadingText']}>処理中...</p>
        </div>
      )}

      <header className={styles['video-edit-header']}>
        <div className={styles['video-edit-headerTitle']}>
          {isMobile && (
            <button
              className={styles['video-edit-backButton']}
              onClick={() => navigate('/my-videos')}
              aria-label="戻る"
            >
              <FaArrowLeft />
            </button>
          )}
          <h1 className={styles['video-edit-pageTitle']}>動画の編集</h1>
        </div>

        <div className={styles['video-edit-headerActions']}>
          {!isMobile && (
            <div className={styles['video-edit-breadcrumb']}>
              <span
                className={styles['video-edit-breadcrumbLink']}
                onClick={() => navigate('/my-videos')}
              >
                マイビデオ
              </span>
              <span className={styles['video-edit-breadcrumbSeparator']}>/</span>
              <span className={styles['video-edit-breadcrumbTitle']}>{title || '動画の編集'}</span>
            </div>
          )}

          {/* ヘッダー右端のボタン群 */}
          <div className={styles['video-edit-headerButtons']}>
            <button
              type="button"
              className={styles['video-edit-headerCancelButton']}
              onClick={() => {
                if (isFormDirty || hasImageChanges) {
                  setShowCancelDialog(true);
                } else {
                  navigate('/my-videos');
                }
              }}
              data-testid="video-edit__header-cancel-button"
            >
              キャンセル
            </button>
            <button
              type="button"
              className={`${styles['video-edit-headerSaveButton']} ${isFormDirty || hasImageChanges ? styles['video-edit-headerSaveButton--dirty'] : ''}`}
              onClick={handleSave}
              disabled={isImageSaving}
              data-testid="video-edit__header-save-button"
            >
              {isImageSaving ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      </header>

      {/* スマートフォン表示 */}
      {isMobile && (
        <>
          {renderMobileTabs()}
          <div className={styles['video-edit-tab-content']}>
            {renderMobileContent()}
          </div>

          {/* FABスタイルの保存ボタン - 並べ替えモードでは非表示 */}
          {!isReorderMode && (
            <button
              type="button"
              className={`${styles['video-edit-fabSaveButton']} ${hasImageChanges ? styles['video-edit-fabSaveButton--dirty'] : ''}`}
              onClick={handleSave}
              disabled={isImageSaving}
              aria-label="変更を保存"
              data-testid="video-edit__save-fab-button"
            >
              <FaSave />
              {hasImageChanges && (
                <span className={styles['fab-badge']}>
                  {pendingDeletions.length + pendingAdditions.length}
                </span>
              )}
            </button>
          )}
        </>
      )}

      {/* デスクトップ表示 */}
      {!isMobile && (
        <div className={styles['video-edit-singleColumnLayout']}>
          <div className={styles['video-edit-card']}>
            <h3 className={styles['video-edit-sectionTitle']}>
              <FaInfo />
              基本情報
            </h3>
            <div className={styles['video-edit-fadeIn']}>
              <VideoEditBasicInfoSection
                title={title}
                setTitle={setTitle}
                description={description}
                setDescription={setDescription}
                visibility={visibility as VisibilityType}
                setVisibility={setVisibility as (v: VisibilityType) => void}
                video={basicSectionVideo}
              />
            </div>
          </div>

          {/* セパレーター */}
          <div className={styles['video-edit-separator']}></div>

          <div className={styles['video-edit-card']}>
            <h3 className={styles['video-edit-sectionTitle']}>
              <FaStethoscope />
              診療科・分類
            </h3>
            <p className={styles['video-edit-sectionDescription']}>
              診療科と、部位や処置などの分類タグを付けると、あとから探しやすくなります。<br />
              非公開のままなら未設定でも構いません。
            </p>
            <div className={styles['video-edit-fadeIn']}>
              <VideoEditSpecialtySection
                specialties={specialties}
                selectedSpecialtyId={selectedSpecialtyId}
                handleSpecialtyChange={handleSpecialtyChange}
                attributes={specialtyAttributes}
                setAttributes={setSpecialtyAttributes}
                suggestedKeys={suggestedKeys}
              />
            </div>
          </div>

          {/* セパレーター */}
          <div className={styles['video-edit-separator']}></div>

          <div className={styles['video-edit-card']}>
            <h3 className={styles['video-edit-sectionTitle']}>
              <FaTag />
              タグ
            </h3>
            <div className={styles['video-edit-fadeIn']}>
              <VideoEditTagSection
                tags={tags}
                setTags={setTags}
                inputValue={inputValue}
                setInputValue={setInputValue}
                isComposing={isComposing}
                setIsComposing={setIsComposing}
              />
            </div>
          </div>

          {/* セパレーター */}
          <div className={styles['video-edit-separator']}></div>

          <div className={styles['video-edit-card']}>
            <h3 className={styles['video-edit-sectionTitle']}>
              <FaCamera />
              術前後画像
            </h3>
            <p className={styles['video-edit-sectionDescription']}>
              術前後画像などを入れることで、動画の参考・共有になります。<br />最大5枚まで追加でき、ドラッグ&ドロップで表示順の並べ替えが可能です。
            </p>
            <div className={styles['video-edit-fadeIn']}>
              <VideoEditImagesSection
                video={imageSectionVideo}
                videoImages={convertedVideoImages}
                allImages={convertedVideoImages}
                handleDeleteImage={handleDeleteImage}
                handleImageFileChange={handleImageFileChange}
                onReorderModeChange={handleReorderModeChange}
                onCommitPendingDeletionsChange={setCommitPendingDeletions}
                toggleDeletionStatus={toggleDeletionStatus}
                isPendingDeletion={isPendingDeletion}
                removeImage={removeImage}
                onImageOrderChange={handleImageOrderChange}
                isVideoProcessing={isVideoProcessing}
              />
            </div>
          </div>

          {/* セパレーター */}
          <div className={styles['video-edit-separator']}></div>

          <div className={styles['video-edit-card']}>
            <h3 className={styles['video-edit-sectionTitle']}>
              <FaFilePdf />
              資料
            </h3>
            <p className={styles['video-edit-sectionDescription']}>
              PDFの資料を付けられます。最大3件・1件100MBまで。詳細画面でページ送り・拡大縮小・全画面表示ができます。
            </p>
            <div className={styles['video-edit-fadeIn']}>
              <VideoEditMaterialsSection
                videoId={String(video.id)}
                isVideoProcessing={isVideoProcessing}
              />
            </div>
          </div>
        </div>
      )}

      {/* キャンセル確認ダイアログ */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        title="編集内容の確認"
        message="編集内容が保存されていません。変更を破棄してマイビデオ画面に戻りますか？"
        confirmLabel="破棄して移動"
        cancelLabel="キャンセル"
        onConfirm={() => {
          setShowCancelDialog(false);
          navigate('/my-videos');
        }}
        onCancel={() => setShowCancelDialog(false)}
      />
    </div>
  );
};

// VideoEditコンポーネントをPrompt機能でラップ
const VideoEdit: React.FC<VideoEditProps> = () => {
  return (
    <ConfirmDialogProvider>
      <VideoEditComponent />
    </ConfirmDialogProvider>
  );
};

export default VideoEdit;
